import React from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import { setSelectedEngine, setActiveRunId } from '@state/slices/runSlice';
import type { EngineId } from '@contracts/index';
import {
  createRunSpecificationSnapshot,
  validateRunPrerequisites
} from '@contracts/index';
import { EngineRegistry, RunExecutionManager } from '@services/engine';
import { validateCfdSetup } from '../../validation/cfdValidation';

export interface RunMonitorPanelProps {
  onSelectSubroute?: (route: string) => void;
}

/**
 * RunMonitorPanel (Gate 3 Implementation)
 *
 * Provides:
 * - Direct Run Creation workflow in the dock
 * - Engine Selection per run ([ Select engine ] without silent defaults)
 * - Snapshot preview and adapter validation status
 * - Authoritative lifecycle state monitor for submitted runs (QUEUED -> RUNNING -> COMPLETED)
 */
export const RunMonitorPanel: React.FC<RunMonitorPanelProps> = () => {
  const dispatch = useAppDispatch();

  const study = useAppSelector((state) => state.study.study);
  const activeScenario = study.scenarios[study.activeScenarioId];

  const geometryEntities = useAppSelector((state) => state.geometry.entities);
  const geometryMasses = Object.values(geometryEntities).filter(
    (g): g is import('@contracts/index').BuildingMassGeometry => g.type === 'building_mass'
  );

  const runs = useAppSelector((state) => state.run.runs);
  const runOrder = useAppSelector((state) => state.run.runOrder);
  const activeRunId = useAppSelector((state) => state.run.activeRunId);
  const selectedEngineId = useAppSelector((state) => state.run.selectedEngineId);
  const nextRunSequence = useAppSelector((state) => state.run.nextRunSequence);

  const activeRun = activeRunId ? runs[activeRunId] : runOrder.length > 0 ? runs[runOrder[0]] : null;

  // Resume status polling on mount for active runs in flight
  React.useEffect(() => {
    Object.values(runs).forEach((r) => {
      if (r.status === 'QUEUED' || r.status === 'PREPARING' || r.status === 'RUNNING') {
        RunExecutionManager.pollRunStatus(r.runId, r.engine, dispatch);
      }
    });
  }, []);

  const g1Validation = validateCfdSetup(activeScenario?.cfdSetup, geometryMasses.length);
  const engineDescriptors = EngineRegistry.getAllEngineDescriptors();

  const prerequisites = validateRunPrerequisites({
    engine: selectedEngineId,
    windSpeed: activeScenario?.cfdSetup.wind.speed ?? null,
    windDirection: activeScenario?.cfdSetup.wind.direction ?? null,
    domain: activeScenario?.cfdSetup.domain ?? null,
    geometryMasses,
    boundaryConditions: activeScenario?.cfdSetup.boundaryConditions ?? null
  });

  const adapterValidation = React.useMemo(() => {
    if (!selectedEngineId || !activeScenario || geometryMasses.length === 0) return null;
    if (activeScenario.cfdSetup.wind.speed === null || activeScenario.cfdSetup.wind.direction === null) return null;

    const draftSpec = createRunSpecificationSnapshot({
      runId: 'DRAFT',
      studyId: study.id,
      scenarioId: activeScenario.id,
      scenarioName: activeScenario.name,
      engine: selectedEngineId,
      windSpeed: activeScenario.cfdSetup.wind.speed,
      windDirection: activeScenario.cfdSetup.wind.direction,
      windConvention: activeScenario.cfdSetup.wind.convention,
      domain: activeScenario.cfdSetup.domain,
      geometryMasses,
      boundaryConditions: activeScenario.cfdSetup.boundaryConditions
    });

    return EngineRegistry.validateSpecification(selectedEngineId, draftSpec);
  }, [selectedEngineId, activeScenario, geometryMasses, study.id]);

  const canCreateRun =
    g1Validation.isReady &&
    selectedEngineId !== null &&
    prerequisites.isValid &&
    (adapterValidation ? adapterValidation.canRepresent : false);

  const handleCreateRun = () => {
    if (!canCreateRun || !selectedEngineId || !activeScenario) return;

    const padNum = String(nextRunSequence).padStart(6, '0');
    const runId = `SPIN-RUN-${padNum}`;

    const immutableSnapshot = createRunSpecificationSnapshot({
      runId,
      studyId: study.id,
      scenarioId: activeScenario.id,
      scenarioName: activeScenario.name,
      engine: selectedEngineId,
      windSpeed: activeScenario.cfdSetup.wind.speed!,
      windDirection: activeScenario.cfdSetup.wind.direction!,
      windConvention: activeScenario.cfdSetup.wind.convention,
      domain: activeScenario.cfdSetup.domain,
      geometryMasses,
      boundaryConditions: activeScenario.cfdSetup.boundaryConditions
    });

    RunExecutionManager.executeRun(immutableSnapshot, dispatch);
  };

  return (
    <div
      className="ssw-dock-panel"
      role="tabpanel"
      id="ssw-dock-panel-run-monitor"
      aria-labelledby="ssw-dock-tab-run-monitor"
    >
      <div className="ssw-dock-panel-header">
        <div className="ssw-dock-panel-title-group">
          <span className="ssw-dock-panel-kicker">SOLVER EXECUTION & ADAPTER CONTRACT</span>
          <h3 className="ssw-dock-panel-title">Run Monitor</h3>
        </div>
        <div className="ssw-dock-panel-badge" style={{ background: 'rgba(47, 120, 196, 0.1)', color: '#2450A6', fontWeight: 600 }}>
          G2: {runOrder.length} RUNS RECORDED
        </div>
      </div>

      <div className="ssw-dock-panel-body" style={{ overflowY: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '16px', height: '100%' }}>
          {/* Left Column: Compact Run Setup */}
          <div
            style={{
              border: '1px solid rgba(47, 120, 196, 0.18)',
              borderRadius: '4px',
              background: '#ffffff',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}
          >
            <div
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: '9.5px',
                fontWeight: 700,
                color: '#2450A6',
                letterSpacing: '0.08em',
                borderBottom: '1px solid rgba(47, 120, 196, 0.1)',
                paddingBottom: '4px'
              }}
            >
              RUN SETUP
            </div>

            <div style={{ fontSize: '11px' }}>
              <div style={{ color: '#64748b', fontSize: '9.5px', fontWeight: 600 }}>Scenario</div>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{activeScenario?.name || 'Baseline'}</div>
            </div>

            <div>
              <label
                htmlFor="dock-cfd-engine-selector"
                style={{ display: 'block', fontSize: '9.5px', color: '#64748b', fontWeight: 600, marginBottom: '2px' }}
              >
                Engine <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="dock-cfd-engine-selector"
                value={selectedEngineId || ''}
                onChange={(e) => dispatch(setSelectedEngine((e.target.value as EngineId) || null))}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  borderRadius: '3px',
                  border: selectedEngineId ? '1px solid #2F78C4' : '1px solid #f59e0b',
                  fontSize: '11px',
                  color: '#0f172a'
                }}
              >
                <option value="" disabled>
                  [ Select engine ]
                </option>
                {engineDescriptors.map((desc) => (
                  <option key={desc.id} value={desc.id}>
                    {desc.displayName} ({desc.availability === 'available' ? '● Available' : '● Configured'})
                  </option>
                ))}
              </select>
            </div>

            {/* Read-Only Spec Summary */}
            <div
              style={{
                background: 'rgba(248, 246, 241, 0.8)',
                border: '1px solid rgba(47, 120, 196, 0.1)',
                borderRadius: '3px',
                padding: '6px 8px',
                fontSize: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '3px'
              }}
            >
              <div>
                <span style={{ color: '#64748b' }}>Wind: </span>
                {activeScenario?.cfdSetup.wind.speed !== null && activeScenario?.cfdSetup.wind.direction !== null ? (
                  <strong>{activeScenario.cfdSetup.wind.speed.toFixed(1)} m/s @ {activeScenario.cfdSetup.wind.direction.toFixed(0)}°</strong>
                ) : (
                  <strong style={{ color: '#d97706' }}>Not defined</strong>
                )}
              </div>
              <div>
                <span style={{ color: '#64748b' }}>Domain: </span>
                <strong>{activeScenario?.cfdSetup.domain.width} × {activeScenario?.cfdSetup.domain.length} × {activeScenario?.cfdSetup.domain.height} m</strong>
              </div>
              <div>
                <span style={{ color: '#64748b' }}>Geometry: </span>
                <strong>{geometryMasses.length} building mass(es)</strong>
              </div>
              <div>
                <span style={{ color: '#64748b' }}>Boundary Conditions: </span>
                <strong>Configured</strong>
              </div>
            </div>

            {/* Error / Validation Warning */}
            {!selectedEngineId && (
              <div style={{ fontSize: '10px', color: '#b45309', background: 'rgba(245, 158, 11, 0.1)', padding: '4px 6px', borderRadius: '3px' }}>
                Cannot create run. Engine selection required.
              </div>
            )}
            {selectedEngineId && adapterValidation && !adapterValidation.canRepresent && (
              <div style={{ fontSize: '9.5px', color: '#b91c1c', background: 'rgba(239, 68, 68, 0.1)', padding: '4px 6px', borderRadius: '3px' }}>
                <strong>ENGINE CANNOT REPRESENT SPECIFICATION:</strong>
                <div>{adapterValidation.errors.join('; ')}</div>
              </div>
            )}

            <button
              type="button"
              id="btn-dock-create-run"
              disabled={!canCreateRun}
              onClick={handleCreateRun}
              style={{
                width: '100%',
                padding: '7px 10px',
                borderRadius: '3px',
                border: 'none',
                background: canCreateRun ? '#2450A6' : '#cbd5e1',
                color: canCreateRun ? '#ffffff' : '#94a3b8',
                fontWeight: 700,
                fontSize: '11px',
                cursor: canCreateRun ? 'pointer' : 'not-allowed'
              }}
            >
              CREATE RUN
            </button>
          </div>

          {/* Right Column: Execution Monitor & Immutable Snapshot Inspector */}
          <div
            style={{
              border: '1px solid rgba(47, 120, 196, 0.18)',
              borderRadius: '4px',
              background: '#ffffff',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(47, 120, 196, 0.1)',
                paddingBottom: '4px'
              }}
            >
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '9.5px',
                  fontWeight: 700,
                  color: '#2450A6',
                  letterSpacing: '0.08em'
                }}
              >
                EXECUTION MONITOR & RUN SNAPSHOTS
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {runOrder.length > 1 && (
                  <select
                    value={activeRun?.runId || ''}
                    onChange={(e) => dispatch(setActiveRunId(e.target.value))}
                    style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      border: '1px solid rgba(47, 120, 196, 0.2)',
                      background: '#ffffff',
                      color: '#0f172a'
                    }}
                  >
                    {runOrder.map((id) => (
                      <option key={id} value={id}>
                        {id} ({runs[id]?.engine.toUpperCase()})
                      </option>
                    ))}
                  </select>
                )}
                <span style={{ fontSize: '9.5px', color: '#64748b' }}>
                  {runOrder.length} Recorded Runs
                </span>
              </div>
            </div>

            {activeRun ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '11px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
                      {activeRun.runId}
                    </span>
                    <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '11px' }}>
                      ({activeRun.scenarioName} • {activeRun.engine.toUpperCase()})
                    </span>
                  </div>
                  <span
                    style={{
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: '9.5px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '3px',
                      background:
                        activeRun.status === 'COMPLETED'
                          ? 'rgba(22, 163, 74, 0.15)'
                          : activeRun.status === 'FAILED'
                            ? 'rgba(239, 68, 68, 0.15)'
                            : activeRun.status === 'RUNNING' || activeRun.status === 'PREPARING'
                              ? 'rgba(37, 99, 235, 0.15)'
                              : 'rgba(245, 158, 11, 0.15)',
                      color:
                        activeRun.status === 'COMPLETED'
                          ? '#15803d'
                          : activeRun.status === 'FAILED'
                            ? '#b91c1c'
                            : activeRun.status === 'RUNNING' || activeRun.status === 'PREPARING'
                              ? '#1d4ed8'
                              : '#b45309'
                    }}
                  >
                    STATUS: {activeRun.status}
                  </span>
                </div>

                {/* Error Banner if Failed */}
                {activeRun.status === 'FAILED' && (
                  <div
                    style={{
                      background: 'rgba(254, 242, 242, 0.9)',
                      border: '1px solid #f87171',
                      borderRadius: '4px',
                      padding: '8px 10px',
                      fontSize: '11px',
                      color: '#991b1b'
                    }}
                  >
                    <strong>EXECUTION FAILED: </strong>
                    <span>{activeRun.error || 'Unknown solver execution failure.'}</span>
                  </div>
                )}

                {/* Result Provenance Banner if Completed */}
                {activeRun.status === 'COMPLETED' && (
                  <div
                    id="run-monitor-result-banner"
                    style={{
                      background: 'rgba(240, 253, 244, 0.9)',
                      border: '1px solid #86efac',
                      borderRadius: '4px',
                      padding: '8px 10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      fontSize: '11px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, color: '#166534', fontFamily: 'ui-monospace, monospace', fontSize: '10px' }}>
                        FIELD SOURCE: AUTHORITATIVE CFD
                      </span>
                      <span style={{ fontWeight: 600, color: '#15803d', fontSize: '10px' }}>
                        RESULT AVAILABLE
                      </span>
                    </div>
                    <div style={{ color: '#14532d', fontSize: '10.5px' }}>
                      Engine: <strong>{activeRun.engine.toUpperCase()}</strong> | Vectors: <strong>{activeRun.resultSummary?.pointCount?.toLocaleString() || '9,408'}</strong>
                    </div>
                    {activeRun.resultSummary?.provenanceSummary && (
                      <div style={{ color: '#4b5563', fontSize: '9.5px', fontStyle: 'italic' }}>
                        {activeRun.resultSummary.provenanceSummary}
                      </div>
                    )}
                  </div>
                )}

                {/* Immutable Spec Snapshot View */}
                <div
                  style={{
                    background: 'rgba(248, 246, 241, 0.7)',
                    border: '1px solid rgba(47, 120, 196, 0.12)',
                    borderRadius: '4px',
                    padding: '10px'
                  }}
                >
                  <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '9.5px', color: '#2450A6', fontWeight: 700, marginBottom: '6px' }}>
                    FROZEN SCIENTIFIC SPECIFICATION (SPEC v{activeRun.specificationVersion})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '10.5px' }}>
                    <div>
                      <span style={{ color: '#64748b' }}>Wind Speed: </span>
                      <strong>{activeRun.specification.wind.speed.toFixed(1)} m/s</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Wind Direction: </span>
                      <strong>{activeRun.specification.wind.direction.toFixed(0)}°</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Coordinate System: </span>
                      <strong>{activeRun.specification.coordinateSystem}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Domain Width (X): </span>
                      <strong>{activeRun.specification.domain.width} m</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Domain Length (Z): </span>
                      <strong>{activeRun.specification.domain.length} m</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Domain Height (Y): </span>
                      <strong>{activeRun.specification.domain.height} m</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Building Masses: </span>
                      <strong>{activeRun.specification.geometry.count}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Inlet Boundary: </span>
                      <strong>{activeRun.specification.boundaryConditions.inlet}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Result Status: </span>
                      <strong style={{ color: activeRun.status === 'COMPLETED' ? '#16a34a' : activeRun.status === 'FAILED' ? '#dc2626' : '#d97706' }}>
                        {activeRun.status === 'COMPLETED' ? 'AVAILABLE' : activeRun.status === 'FAILED' ? 'FAILED' : 'PENDING'}
                      </strong>
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic' }}>
                  Scientific Immutability Guaranteed: This run specification snapshot is permanently decoupled from subsequent scenario edits.
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: '11px',
                  background: '#f8fafc',
                  borderRadius: '4px',
                  border: '1px dashed rgba(47, 120, 196, 0.2)'
                }}
              >
                No active run selected. Select an engine and click <strong>CREATE RUN</strong> to snapshot a specification.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunMonitorPanel;
